import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Drawer,
  Text,
  Toast,
  Icons,
  Checkbox,
  useToggle,
} from "@pushpress/shared-components";
import { toast } from "react-toastify";
import { GetCalendars, updateCalendars } from "../../../apis/googleSyncApi";
import DrawerPanel from "../../../components/common/DrawerPanel/DrawerPanel";
import { AppContext } from "../../../context/app";
import { defaultToastOptions } from "../../../utils/misc";
import { useGetCalendars } from "../../../apis/googleSyncApi.hooks";
import CalendarIcon from "../../../assets/google-calendar.svg";
import styles from "./CalendarSelectionDrawer.module.scss";

export type CalendarSelectionDrawerProps = {
  staffId?: string;
  visible: boolean;
  onClose?: VoidFunction;
  onSuccess?: VoidFunction;
};

const CalendarSelectionDrawer = (props: CalendarSelectionDrawerProps) => {
  const { visible } = props;
  return (
    <Drawer
      visible={visible}
      anchor="right"
      width="400px"
      disableClickAway
      disableEscapeKeyDown
    >
      <CalendarSelectionDrawerContent {...props} />
    </Drawer>
  );
};

const CalendarSelectionDrawerContent = (
  props: CalendarSelectionDrawerProps,
) => {
  const { staffId, onSuccess, onClose } = props;

  const { client } = useContext(AppContext);

  const { data: calendarsData } = useGetCalendars(staffId!, {
    enabled: !!staffId,
    staleTime: 0,
  });

  const [calendars, setCalendars] = useState<GetCalendars["calendars"]>([]);

  const [isSaving, savingToggle] = useToggle(false);

  const onToggle = (calendarId: string) => {
    setCalendars((prevCalendars) => {
      return prevCalendars.map((calendar) => {
        if (calendar.id === calendarId) {
          return {
            ...calendar,
            visible: !calendar.visible,
          };
        }
        return calendar;
      });
    });
  };

  const handleSave = async () => {
    if (!staffId) {
      return;
    }
    try {
      savingToggle.on();
      const response = await updateCalendars(staffId, client.uuid, calendars);
      if (response.status === 200) {
        toast.success("Calendars successfully updated", defaultToastOptions);
      } else {
        throw new Error("Error during calendars update");
      }
    } catch (err) {
      toast.error(err, defaultToastOptions);
    } finally {
      savingToggle.off();
      onSuccess?.();
    }
  };

  const email = useMemo(() => {
    if (!calendarsData?.calendars) {
      return "--";
    }
    // get the primary calendar used to connect the account
    const primaryGoogleCalendar = calendarsData?.calendars.find(
      (c) => c.primary,
    );
    return primaryGoogleCalendar?.id || "--";
  }, [calendarsData]);

  // Filter out the primary calendar if it's not visible and set the calendars
  useEffect(() => {
    if (calendarsData?.calendars) {
      const filtered = calendarsData.calendars.filter((cal) => {
        if (calendarsData.primaryCalendar === cal.id && !cal.visible) {
          return false;
        }
        return true;
      });
      setCalendars(filtered);
    }
  }, [calendarsData]);

  const loading = !calendarsData;

  return (
    <DrawerPanel
      title="Which of your calendars should we check for blocking availability?"
      titlePosition="bottom"
      loading={loading}
      saving={isSaving}
      onSave={handleSave}
      onClose={() => onClose?.()}
    >
      <div className={styles.email}>
        <CalendarIcon className={styles.icon} />
        <Text variant="body-lg" color="wolf-800">
          {email}
        </Text>
      </div>
      <div className={styles.checkboxes}>
        {calendars.map((c) => (
          <Checkbox
            checked={c.visible}
            label={c.name}
            onToggle={() => onToggle(c.id)}
            key={c.id}
          />
        ))}
      </div>
      <Toast
        icon={Icons.Information}
        message="Keep the selected calendars up to date to prevent getting booked when busy. "
        iconColor="success"
      />
    </DrawerPanel>
  );
};

export default CalendarSelectionDrawer;
